// Required M&V plan contents, per FEMP M&V Guidelines 5.0 §2.2.4.
//
// This check emits gaps, not failure-mode flags. Anything on FEMP's own
// required-contents list and absent from the plan is a documented gap — but a
// gap is not yet a dispute cause, and calling it one would overstate what a
// keyword search can know.
//
// The three-way verdict matters more than the checklist. A keyword search over a
// PDF text layer cannot prove absence, so "absent" is reserved for items with no
// signal at all, and anything with partial evidence is "uncertain". A tool that
// says "absent" when it means "I could not find it" is worse than useless to
// someone who has to defend the finding.

import type {
  CompletenessGap,
  CompletenessItem,
  CompletenessStatus,
  SourceDocument,
} from '../../types';
import { buildLocator, ecmScopes } from './locate';

export const CHECK_ID = 'completeness-check';

interface Requirement extends CompletenessItem {
  /** An explicit phrase for this requirement. A hit means present. */
  strong: RegExp[];
  /** A partial signal. A hit without a strong hit means uncertain, not absent. */
  weak: RegExp[];
}

const PROJECT_ITEMS: Requirement[] = [
  {
    id: 'project-savings-overview',
    scope: 'project',
    label: 'Savings overview',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, project-wide',
    strong: [
      /\b(?:proposed )?annual savings (?:overview|summary)\b/i,
      /\bsavings (?:overview|summary)\b/i,
    ],
    weak: [/\bproposed annual savings\b/i, /\btotal (?:annual )?savings\b/i],
  },
  {
    id: 'project-mv-schedule',
    scope: 'project',
    label: 'M&V activity schedule',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, project-wide',
    strong: [
      /\bschedule (?:of|and reporting for) (?:M&V|verification)\b/i,
      /\bM&V (?:activity )?schedule\b/i,
      /\bschedule of M&V submissions\b/i,
    ],
    weak: [/\bwithin \d+ days\b/i, /\bannual report\b/i, /\bpost-installation report\b/i],
  },
  {
    id: 'project-witnessing',
    scope: 'project',
    label: 'Witnessing and customer sign-off requirements',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, project-wide',
    strong: [/\bwitness(?:ed|ing|es)?\b/i, /\bsign[- ]?off\b/i],
    weak: [
      /\bcustomer (?:acceptance|approval|concurrence)\b/i,
      /\bnotification of acceptance\b/i,
      /\breview and approve\b/i,
    ],
  },
  {
    id: 'project-rates',
    scope: 'project',
    label: 'Utility rates and cost-savings method',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, project-wide',
    strong: [
      /\b(?:electric |utility )?rate schedule\b/i,
      /\benergy(?:,| and) water(?:,)? and .{0,40}rate data\b/i,
      /\bcost savings (?:method|calculation|methodology)\b/i,
    ],
    weak: [/\$\s?0?\.\d+\s*\/?\s*kWh\b/i, /\bblended rate\b/i, /\bescalation rate\b/i],
  },
  {
    id: 'project-om-reporting',
    scope: 'project',
    label: 'O&M reporting responsibilities',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, project-wide',
    strong: [
      /\bO&M reporting\b/i,
      /\b(?:operations|preventive maintenance).{0,60}reporting requirements\b/i,
      /\breport on all maintenance work\b/i,
    ],
    weak: [/\bmaintenance records\b/i, /\bpreventive maintenance\b/i],
  },
];

const ECM_ITEMS: Requirement[] = [
  {
    id: 'ecm-baseline',
    scope: 'ecm',
    label: 'Baseline conditions and data collected',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [/\b(?:energy and water )?baseline development\b/i, /\bbaseline conditions\b/i],
    weak: [/\bbaseline\b/i],
  },
  {
    id: 'ecm-assumptions',
    scope: 'ecm',
    label: 'Documentation of all assumptions and sources of data',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [
      /\bdocumentation of all assumptions\b/i,
      /\bassumptions and sources of data\b/i,
      /\bsources? of data\b/i,
    ],
    weak: [/\bassum(?:es|ed|ption)\b/i],
  },
  {
    id: 'ecm-engineering',
    scope: 'ecm',
    label: 'Engineering analysis performed',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [/\bengineering analysis\b/i],
    weak: [/\bequation\b/i, /\bcalculated as\b/i, /\bregression\b/i, /\bmodel(?:led|ed)?\b/i],
  },
  {
    id: 'ecm-savings-method',
    scope: 'ecm',
    label: 'Savings calculation method',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [
      /\bsavings calculation (?:method|methods|methodology)\b/i,
      /\bcalculation methods?\b/i,
    ],
    weak: [/\bsavings (?:are|will be) calculated\b/i],
  },
  {
    id: 'ecm-om-savings',
    scope: 'ecm',
    label: 'O&M or other cost savings claimed',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [
      /\boperations and maintenance and other cost savings\b/i,
      /\bO&M (?:and other )?cost savings\b/i,
    ],
    weak: [/\bother cost savings\b/i, /\bno O&M savings\b/i],
  },
  {
    id: 'ecm-proposed-savings',
    scope: 'ecm',
    label: 'Proposed energy and cost savings',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [
      /\bproposed (?:annual )?(?:energy and )?(?:water and )?(?:cost )?savings\b/i,
      /\bproposed annual savings for ECM\b/i,
    ],
    weak: [/\bannual savings\b/i],
  },
  {
    id: 'ecm-post-install',
    scope: 'ecm',
    label: 'Post-installation verification activities',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [/\bpost-installation (?:M&V )?(?:activities|verification)\b/i],
    weak: [/\bpost-installation\b/i],
  },
  {
    id: 'ecm-performance-period',
    scope: 'ecm',
    label: 'Performance-period verification activities',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [/\bperformance[- ]period (?:M&V )?(?:verification )?activities\b/i],
    weak: [/\bperformance period\b/i],
  },
  {
    id: 'ecm-adjustments',
    scope: 'ecm',
    label: 'Anticipated adjustments, with parameters and justification',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [
      /\banticipated adjustments\b/i,
      /\bbaseline adjustments?\b/i,
      /\bnon-?routine adjustments?\b/i,
    ],
    weak: [/\badjust(?:ed|ment|ments)\b/i],
  },
  {
    id: 'ecm-report-format',
    scope: 'ecm',
    label: 'Content and format of required reports',
    reference: 'FEMP M&V Guidelines 5.0 §2.2.4, ECM-level',
    strong: [
      /\b(?:content and format|format and content)\b/i,
      /\bcontents? of (?:the )?(?:required )?reports?\b/i,
    ],
    weak: [/\bwill be reported\b/i, /\breporting requirements\b/i],
  },
];

export const REQUIREMENTS = [...PROJECT_ITEMS, ...ECM_ITEMS];

function classify(
  requirement: Requirement,
  haystack: string,
): { status: CompletenessStatus; matched: string | null; index: number } {
  for (const pattern of requirement.strong) {
    const m = pattern.exec(haystack);
    if (m) return { status: 'present', matched: m[0], index: m.index };
  }
  for (const pattern of requirement.weak) {
    const m = pattern.exec(haystack);
    if (m) return { status: 'uncertain', matched: m[0], index: m.index };
  }
  return { status: 'absent', matched: null, index: -1 };
}

function receiptFor(
  requirement: Requirement,
  status: CompletenessStatus,
  matched: string | null,
  scopeLabel: string,
): CompletenessGap['receipt'] {
  const result =
    status === 'present'
      ? `Found in ${scopeLabel} — matched "${matched}".`
      : status === 'uncertain'
        ? `A partial signal ("${matched}") appears in ${scopeLabel}, but no explicit statement of this requirement was found. Confirm by reading the section; this check cannot tell the difference between an item covered in different words and an item missing.`
        : `No phrase matching this requirement was found in ${scopeLabel}.`;

  return {
    kind: 'checklist',
    inputs: [
      { label: 'Requirement', value: requirement.label },
      { label: 'Source', value: requirement.reference },
      { label: 'Scope searched', value: scopeLabel },
      {
        label: 'Explicit phrases sought',
        value: requirement.strong.map((r) => r.source).join(' | '),
      },
      {
        label: 'Partial signals sought',
        value: requirement.weak.map((r) => r.source).join(' | '),
      },
    ],
    computation:
      'Explicit phrase found → present. Only a partial signal → uncertain. Nothing → absent.',
    result,
  };
}

/**
 * Returns a gap for every requirement that is not clearly present. Items found
 * are not returned — the checklist reports what is missing or doubtful.
 */
export function completenessCheck(doc: SourceDocument): CompletenessGap[] {
  const locator = buildLocator(doc);
  const gaps: CompletenessGap[] = [];
  let counter = 0;

  // Project-wide items are searched across the whole document, including
  // section titles, since a heading is often the only statement of the item.
  const titles = doc.sections.map((s) => `${s.number} ${s.title}`).join('\n');
  const projectHaystack = `${titles}\n${doc.rawText}`;

  for (const requirement of PROJECT_ITEMS) {
    const { status, matched, index } = classify(requirement, projectHaystack);
    if (status === 'present') continue;

    gaps.push({
      id: `gap-${CHECK_ID}-${++counter}`,
      checkId: CHECK_ID,
      itemId: requirement.id,
      scope: 'project',
      label: requirement.label,
      reference: requirement.reference,
      status,
      quote: null,
      page: null,
      receipt: receiptFor(requirement, status, matched, 'the whole document'),
    });
    void index;
  }

  // ECM-level items are searched inside each ECM, because "present somewhere in
  // the document" is not the requirement — each ECM must carry them.
  const scopes = ecmScopes(doc);

  for (const scope of scopes) {
    const scopeTitles = scope.sections
      .map((s) => `${s.number} ${s.title}`)
      .join('\n');
    const haystack = `${scopeTitles}\n${scope.text}`;

    for (const requirement of ECM_ITEMS) {
      const { status, matched } = classify(requirement, haystack);
      if (status === 'present') continue;

      // FEMP lists these at ECM level, but a plan may state one once for the
      // whole project — the report format, for instance. Saying only "absent
      // from ECM 2" would send a reviewer looking for something that is in the
      // document, three sections earlier.
      const elsewhere = classify(requirement, `${titles}\n${doc.rawText}`);
      const foundElsewhere = elsewhere.status === 'present';

      // Locate the partial signal in rawText so the quote stays verbatim.
      let quote: string | null = null;
      let page: number | null = null;
      if (matched) {
        const at = doc.rawText.indexOf(matched);
        if (at >= 0) {
          quote = locator.quoteAround(at, matched.length, 120);
          page = locator.pageOf(at);
        }
      }

      gaps.push({
        id: `gap-${CHECK_ID}-${++counter}`,
        checkId: CHECK_ID,
        itemId: requirement.id,
        scope: 'ecm',
        label: requirement.label,
        reference: requirement.reference,
        status,
        ecmRef: scope.ref,
        quote,
        page,
        receipt: (() => {
          const receipt = receiptFor(requirement, status, matched, scope.ref);
          if (foundElsewhere) {
            receipt.inputs.push({
              label: 'Found elsewhere in the document',
              value: `yes — matched "${elsewhere.matched}" outside this ECM`,
            });
            receipt.result += ` This requirement does appear elsewhere in the document, so it may be stated once for the whole project rather than per ECM — check whether that satisfies the requirement for this measure.`;
          }
          return receipt;
        })(),
      });
    }
  }

  return gaps;
}
